import React, { useEffect, useRef, useState } from 'react';
import Aside from '../Aside/aside';
import UnreadBell from './unreadBell';
import { UnreadProvider } from '../../../msg/unreadContext';
import { useTheme } from '../../../hooks/useTheme';
import { useAuth } from '../../../context/AuthContext';
// Д-17: плашка «вышло обновление» — на ЛЮБОЙ странице кабинета: комната и
// чат владельца живут во вкладке часами, а после окна старый бандл ломает
// приём новых сообщений. Один каркас — одно место монтирования.
import UpdateBanner from '../../../msg/updateBanner';
import './cabinetShell.css';

// WEB-9: единый каркас кабинета — меню слева, шапка сверху, контент под ней.
//
// Зачем компонент, а не «поправить CSS на каждой странице». Каркас был у
// каждой страницы свой: `.test` (грид), `.MapPage` и `.employeePage` (флексы),
// и одинаковый на вид результат держался на трёх независимых наборах правил.
// Любая правка каркаса требовала найти все три — а шапки не было вовсе, и
// переключателю темы негде было жить, кроме отдельной страницы «оформление».
//
// 🔴 Состояние выдвижного меню живёт ЗДЕСЬ, а не в Aside: кнопка-бургер стоит
// в шапке, а панель — соседний элемент. Держать флаг в Aside значило бы
// поднимать его наружу через колбэк из ребёнка в ребёнка.

const CabinetHeaderTheme = () => {
  const { theme, setTheme } = useTheme();
  const dark = theme === 'dark';
  return (
    <button
      type="button"
      className="cabinetIconButton cabinetThemeButton"
      // Подпись говорит, ЧТО СЛУЧИТСЯ по нажатию, а не в какой теме мы сейчас:
      // иконка и так показывает текущее состояние.
      aria-label={dark ? 'Включить светлую тему' : 'Включить тёмную тему'}
      title={dark ? 'Светлая тема' : 'Тёмная тема'}
      onClick={() => setTheme(dark ? 'light' : 'dark')}
    >
      {dark ? (
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 3v2" /><path d="M12 19v2" /><path d="M3 12h2" /><path d="M19 12h2" />
          <path d="m5.6 5.6 1.4 1.4" /><path d="m17 17 1.4 1.4" />
          <path d="m18.4 5.6-1.4 1.4" /><path d="m7 17-1.4 1.4" />
        </svg>
      ) : (
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
        </svg>
      )}
    </button>
  );
};

// Что внутри панели можно поймать фокусом. Список намеренно короткий: в меню
// живут только ссылки и кнопки, экзотику вроде contenteditable сюда тащить
// незачем — она там не появится незаметно.
// Экспортируется РАДИ ТЕСТА (ревью Codex р.4, Low): дублирование списка в
// cabinetShell.test.jsx означало бы, что добавленный сюда вид фокусируемого
// элемента прод обработает, а тест его не заметит.
export const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

// Ширина, на которой меню становится выдвижной панелью. Держим В ОДНОМ месте
// с CSS-медиазапросом: разъехавшись, они дали бы состояние «CSS показывает
// десктопный сайдбар, а JS всё ещё считает, что открыт drawer».
const DRAWER_QUERY = '(max-width: 960px)';

const matchesDrawer = () => (
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DRAWER_QUERY).matches
    : false);

// Есть ли у браузера НАСТОЯЩИЙ `inert`. Где его нет, атрибут — просто буква в
// разметке: он не убирает элементы ни из таба, ни из чтения, и панель надо
// гасить руками (см. эффект ниже).
const INERT_SUPPORTED = typeof HTMLElement !== 'undefined'
  && 'inert' in HTMLElement.prototype;

// Только то, что человек реально может поймать табом. Фильтр нужен не для
// красоты: внутри `.aside` живёт модалка «Выйти?», и её кнопки остаются в
// дереве, пока модалка закрыта (ревью Codex WEB-9 р.2, High).
const visibleFocusable = (panel) => [...panel.querySelectorAll(FOCUSABLE)]
  .filter((el) => {
    if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none';
  });

// `fill` — экран обязан помещаться в окно ЦЕЛИКОМ и прокручивать содержимое
// внутри себя, а не растягивать страницу. Нужен переписке: у неё поле ввода
// внизу, и если страница растёт, ввод уезжает под сгиб, а лента перестаёт
// прокручиваться сама (см. `.cabinetPageFill` в cabinetShell.css).
//
// 🔴 Почему проп, а не общее правило для всех страниц кабинета: обычные
// страницы (таблица точек, настройки) ДОЛЖНЫ расти и прокручиваться окном.
// Ограничив высоту всем, мы бы обрезали их содержимое насовсем.
// WEB-23: `action` — узел страницы в шапке каркаса, слева от колокольчика.
// Нужен замку «Защита переписки»: у экрана переписки нет ни своей шапки, ни
// подвального меню (оно есть только у участника), а отдельная строка под
// замок съела бы ровно ту высоту, ради которой панель и затевалась.
// Без пропа шапка рисуется в точности как раньше.
const CabinetShell = ({ title, subtitle, action = null, floor = true, fill = false, children }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState(matchesDrawer);
  const { username } = useAuth();
  const panelRef = useRef(null);
  const burgerRef = useRef(null);
  const wasOpen = useRef(false);

  // 🔴 Ширина экрана меняется под открытым меню (ревью Codex WEB-9 р.2,
  // Medium): поворот телефона или растянутое окно возвращают ОБЫЧНЫЙ сайдбар,
  // а состояние «drawer открыт» оставалось бы жить — с заблокированной
  // прокруткой страницы и фокусом, запертым в меню.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(DRAWER_QUERY);
    const onChange = (event) => {
      setDrawerMode(event.matches);
      if (!event.matches) setMenuOpen(false);
    };
    // Ширина могла измениться между первым рендером и подпиской — сверяемся
    // с фактическим состоянием, а не ждём первого события.
    onChange(media);
    // 🔴 Старый API обязателен (ревью Codex WEB-9 р.3, High): в Safari до 14 и
    // в старых вебвью у MediaQueryList есть только addListener/removeListener.
    // Голый addEventListener бросал бы TypeError прямо в эффекте — и вместе с
    // подпиской терялось бы всё поведение drawer'а.
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }
    if (typeof media.addListener === 'function') {
      media.addListener(onChange);
      return () => media.removeListener(onChange);
    }
    return undefined;
  }, []);

  // 🔴 Закрытая панель уходит из таба и из дерева доступности НЕМЕДЛЕННО
  // (ревью Codex WEB-9 р.2, Low). CSS-скрытие ждёт конца переезда — 0,22 с, в
  // которые Shift+Tab успевал вернуться в уезжающее меню.
  //
  // 🔴 Одного `inert` мало (ревью Codex WEB-9 р.3, Low): где он не
  // поддержан, атрибут не значит НИЧЕГО — ни для таба, ни для скринридера
  // (`aria-hidden` таб тоже не трогает), и окно в 0,22 с возвращается. Поэтому
  // там же снимаем фокусируемость руками, запоминая прежний tabindex, чтобы
  // вернуть его в точности — включая «его не было».
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const hidden = drawerMode && !menuOpen;
    if (hidden) {
      panel.setAttribute('inert', '');
      panel.setAttribute('aria-hidden', 'true');
    } else {
      panel.removeAttribute('inert');
      panel.removeAttribute('aria-hidden');
    }
    if (INERT_SUPPORTED) return;
    for (const el of panel.querySelectorAll(FOCUSABLE)) {
      if (hidden) {
        if (el.dataset.shellTabindex === undefined) {
          el.dataset.shellTabindex = el.getAttribute('tabindex') ?? '';
        }
        el.setAttribute('tabindex', '-1');
      } else if (el.dataset.shellTabindex !== undefined) {
        const previous = el.dataset.shellTabindex;
        if (previous === '') el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', previous);
        delete el.dataset.shellTabindex;
      }
    }
  }, [drawerMode, menuOpen]);

  // 🔴 Клавиатура и выдвижная панель (ревью Codex WEB-9, High). Уехавшая за
  // край панель скрыта через `visibility` (см. cabinetShell.css) — значит из
  // таб-порядка и из дерева доступности она уходит сама. Но у открытой панели
  // остаются три обязанности, которых CSS не даёт: увести в неё фокус, не
  // выпускать его на страницу под затемнением и закрываться по Esc. Плюс
  // прокрутка: без неё палец листает страницу ПОД панелью, а не панель.
  useEffect(() => {
    if (!menuOpen) {
      if (wasOpen.current) {
        wasOpen.current = false;
        burgerRef.current?.focus();   // вернуть фокус туда, откуда открыли
      }
      return undefined;
    }
    wasOpen.current = true;
    const panel = panelRef.current;
    (panel?.querySelector('.asideClose') || panel)?.focus?.();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        // Внутри панели может быть открыт диалог («Выйти?»). Esc относится к
        // ВЕРХНЕМУ слою: закрыть под ним меню значило бы увезти диалог вместе
        // с вопросом, на который человек ещё не ответил.
        if (panel?.querySelector('.modal.active')) return;
        setMenuOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const items = visibleFocusable(panel);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      // Замыкаем круг сами: панель лежит поверх страницы, и Tab с последнего
      // пункта уводил бы на ссылки под затемнением — туда, куда мышью сейчас
      // не попасть.
      if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  return (
    // WEB-17: провайдер снаружи всего каркаса — непрочитанное нужно и шапке
    // (значок), и меню (раздел «Чат»), а спросить сервер об этом позволено
    // ровно одному месту. Своей разметки провайдер не даёт.
    <UnreadProvider>
    <div
      className={`cabinetPage${menuOpen ? ' cabinetPageMenuOpen' : ''}${fill ? ' cabinetPageFill' : ''}`}
      data-testid="cabinet-page"
    >
      {/* Затемнение под выдвинутым меню — цель для пальца и мыши, и только.
          От скринридера оно спрятано: подпись «Закрыть меню» уже есть у
          крестика в самой панели, и вторая такая же читалась бы как второй
          орган управления. С клавиатуры меню закрывают Esc и тот же крестик. */}
      <button
        type="button"
        className="cabinetScrim"
        aria-hidden="true"
        tabIndex={-1}
        onClick={() => setMenuOpen(false)}
      />

      <Aside floor={floor} open={menuOpen} onClose={() => setMenuOpen(false)}
             panelRef={panelRef} />

      <div className="cabinetMain">
        <header className="cabinetHeader">
          <button
            type="button"
            ref={burgerRef}
            className="cabinetIconButton cabinetBurger"
            aria-label="Открыть меню"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
              <path d="M4 7h16" /><path d="M4 12h16" /><path d="M4 17h16" />
            </svg>
          </button>

          <div className="cabinetTitleBlock">
            <span className="cabinetTitle">{title}</span>
            {subtitle && <span className="cabinetSubtitle">{subtitle}</span>}
          </div>

          <div className="cabinetHeaderActions">
            {action}
            {/* WEB-10: непрочитанное видно с ЛЮБОЙ страницы кабинета, а не
                только со страницы «Мой QR», как было до 31.08. */}
            <UnreadBell />
            <CabinetHeaderTheme />
            {username && (
              <div className="cabinetUser">
                <span className="cabinetUserName">{username}</span>
                <span className="cabinetUserAvatar" aria-hidden="true">
                  {username.slice(0, 1).toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </header>

        <div
          className={`cabinetContent${fill ? ' cabinetContentFill' : ''}`}
          data-testid="cabinet-content"
        >
          {/* Д-17: над содержимым, вне прокрутки страницы (`flex: none`) —
              плашка обязана оставаться на виду и с прокрученной лентой. */}
          <UpdateBanner />
          {children}
        </div>
      </div>
    </div>
    </UnreadProvider>
  );
};

export default CabinetShell;
